import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Paper, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { db } from '../../services/firebase';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import SportsNews from 'components/SportsNews';
import OddsList from '../OddsList';
import TeamBadge from '../TeamBadge';
import { getTeamMark } from '../../data/teamDirectory';
import Promos from 'components/Promos';
import { FeaturedPicks } from 'components/FeaturedPicks';


const formatAmerican = (price) => {
  if (price === undefined || price === null || Number.isNaN(Number(price))) return '';
  const n = Number(price);
  return n > 0 ? `+${n}` : `${n}`;
};

// Pick a representative market line for the Market Watch panel
const getWatchMarket = (game) => {
  const bookmaker = game?.bookmakers?.[0];
  if (!bookmaker) return null;
  const markets = bookmaker.markets || [];
  const totals = markets.find((m) => m.key === 'totals');
  if (totals?.outcomes?.length) {
    const o = totals.outcomes[0];
    return { label: 'Total', detail: `${o.point ?? ''} (${formatAmerican(o.price)})`.trim() };
  }
  const spreads = markets.find((m) => m.key === 'spreads');
  if (spreads?.outcomes?.length) {
    const o = spreads.outcomes[0];
    return { label: 'Spread', detail: `${o.point ?? ''} (${formatAmerican(o.price)})`.trim() };
  }
  const h2h = markets.find((m) => m.key === 'h2h');
  if (h2h?.outcomes?.length) {
    return { label: 'Moneyline', detail: formatAmerican(h2h.outcomes[0].price) };
  }
  return null;
};

const HomeScreen = ({ odds = [], loading: oddsLoading = false }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isPremium, loading: subscriptionLoading } = useSubscription();
  
  const [featuredPicks, setFeaturedPicks] = useState([]);
  const [picksLoading, setPicksLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;
  const MAX_RETRIES = 3;

  // Fetch featured picks from Firestore with retry mechanism and caching
  const fetchFeaturedPicks = useCallback(async () => {
    // Check cache first
    if (lastFetchTime && Date.now() - lastFetchTime < CACHE_DURATION && featuredPicks.length > 0) {
      return; // Use cached data
    }

    setPicksLoading(true);

    try {
      // AI Personality IDs for BettorAI
      const AI_PERSONALITY_IDS = ['bettor-ai-con', 'bettor-ai-agg', 'bettor-ai-bal'];
      
      const handicappersRef = collection(db, 'handicapperPicksV2');
      const q = query(handicappersRef, orderBy('lastPickDate', 'desc'), limit(20));
      const querySnapshot = await getDocs(q);

      const allPicks = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.picks && Array.isArray(data.picks)) {
          // Add handicapper info to each pick and flatten
          const picksWithHandicapper = data.picks.map(pick => ({
            ...pick,
            handicapperName: data.handicapperName || 'Unknown Handicapper',
            handicapperSlug: data.handicapperSlug || doc.id,
            // Add personality metadata if available
            isAI: data.isAI || false,
            personality: pick.personality || data.personality || null,
            personalityName: pick.personalityName || data.personalityName || null,
            personalityColor: pick.personalityColor || data.color || null,
            personalityIcon: pick.personalityIcon || data.icon || null
          }));
          allPicks.push(...picksWithHandicapper);
        }
      });

      // Sort all picks by commence time (game time) for AI personalities, otherwise by creation date
      const sortedPicks = allPicks
        .sort((a, b) => {
          // If both picks have commence time, sort by that (earliest games first)
          if (a.commenceTime && b.commenceTime) {
            return new Date(a.commenceTime) - new Date(b.commenceTime);
          }
          
          // Otherwise, fall back to creation date (newest first)
          const aTime = a.createdAt || 0;
          const bTime = b.createdAt || 0;
          return bTime - aTime;
        });

      setFeaturedPicks(sortedPicks);
      setLastFetchTime(Date.now());
      setRetryCount(0); // Reset retry count on success
    } catch (error) {
      console.error('Error fetching featured picks:', error);

      // Retry logic with exponential backoff
      if (retryCount < MAX_RETRIES) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          fetchFeaturedPicks();
        }, 5000); // Retry every 5 seconds
      } else {
        setFeaturedPicks([]);
      }
    } finally {
      setPicksLoading(false);
    }
  }, [lastFetchTime, featuredPicks.length, retryCount]);

  // Fetch data on component mount and set up periodic refresh
  useEffect(() => {
    fetchFeaturedPicks();

    // Set up periodic refresh every 5 minutes
    const interval = setInterval(() => {
      fetchFeaturedPicks();
    }, CACHE_DURATION);

    return () => clearInterval(interval);
  }, [fetchFeaturedPicks]);

  // Demo home composition data, driven by the live odds passed from the router
  const upcomingGames = [...(odds || [])]
    .filter((g) => g?.commence_time)
    .sort((a, b) => new Date(a.commence_time) - new Date(b.commence_time));
  const watchGames = upcomingGames.slice(0, 4);
  // Featured Matchups are the NEXT marquee games after the Market Watch
  // four (ranked by how many books carry them) — never the same slice.
  const watchIds = new Set(watchGames.map((g) => g.id));
  const featuredGames = upcomingGames
    .filter((g) => !watchIds.has(g.id))
    .sort((a, b) => (b.bookmakers?.length || 0) - (a.bookmakers?.length || 0))
    .slice(0, 4);
  const bookmakerCount = new Set();
  (odds || []).forEach((g) => (g.bookmakers || []).forEach((b) => bookmakerCount.add(b.key || b.title)));
  const quickStats = [
    { value: odds?.length ? String(odds.length) : '—', label: 'Games on the board' },
    { value: '12', label: 'Sports to choose from' },
    { value: bookmakerCount.size ? String(bookmakerCount.size) : '—', label: 'Sportsbooks compared' },
    { value: 'Daily', label: 'BettorAI pick updates' },
  ];

  return (
    <Box>
      {/* Hero + Market Watch (demo home screen) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.5fr 0.65fr' },
          gap: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '6px 28px 6px 28px',
            p: { xs: 3, md: 4 },
            minHeight: { md: 320 },
            color: '#fff',
            background:
              'linear-gradient(135deg, rgba(14, 36, 27, 0.78) 0%, rgba(18, 53, 36, 0.66) 55%, rgba(29, 92, 63, 0.58) 100%), url(/assets/herobanner.JPG) center / cover no-repeat',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0 2px, transparent 2px 14px)',
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#bded54',
                  boxShadow: '0 0 0 4px rgba(189, 237, 84, 0.18)',
                }}
              />
              <Typography
                variant="caption"
                sx={{ letterSpacing: '0.14em', fontWeight: 700, color: '#cfe0d6' }}
              >
                BETTORAI · GAME-BY-GAME INTELLIGENCE
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.05, mb: 2 }}>
              AI Handicappers —
              <br />
              <Box component="span" sx={{ color: '#bded54' }}>
                The Next Evolution of Sports Prediction
              </Box>
            </Typography>
            <Typography sx={{ color: '#d7e5dc', maxWidth: 560, mb: 3 }}>
              Research every matchup with sportsbook comparisons, game context and subscription
              AI analysis built for decisive, responsible bettors.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/handicappers')}
                sx={{
                  bgcolor: '#e8721f',
                  color: '#fff',
                  fontWeight: 800,
                  px: 3,
                  py: 1.25,
                  '&:hover': { bgcolor: '#d1621a' },
                }}
              >
                VIEW OUR PICKS
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/sport/nfl')}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.5)',
                  fontWeight: 700,
                  px: 3,
                  py: 1.25,
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Open NFL matchups
              </Button>
            </Box>
          </Box>
        </Box>

        <Paper sx={{ p: 2.25, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              pb: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.1 }}>
                Market Watch
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Representative lines from the live board
              </Typography>
            </Box>
            <Chip label={`${watchGames.length} markets`} size="small" variant="outlined" />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1.75 }}>
            {watchGames.length > 0 ? (
              watchGames.map((game) => {
                const market = getWatchMarket(game);
                return (
                  <Box
                    key={game.id}
                    onClick={() => navigate('/odds')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TeamBadge teamName={game.away_team} size={22} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {getTeamMark(game.away_team).abbr} @ {getTeamMark(game.home_team).abbr}
                    </Typography>
                    <TeamBadge teamName={game.home_team} size={22} />
                    <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {market ? market.label : 'Lines'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {market ? market.detail : 'updating'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {oddsLoading
                    ? 'Loading the live board…'
                    : 'No games on the board right now — lines appear here the moment books post them.'}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => navigate('/odds')}>
                  Open All Odds
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Quick stats strip */}
      <Paper
        sx={{
          mt: 2,
          px: 2,
          py: 1.75,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
        }}
      >
        {quickStats.map((stat) => (
          <Box key={stat.label} sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', lineHeight: 1 }}>
              {stat.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Paper>

      {/* Featured Picks Section */}
      <Box sx={{ mt: 4 }}>
        <FeaturedPicks picks={featuredPicks} loading={picksLoading} title="Featured Picks" />
      </Box>

      {/* Featured Matchups (demo home games) */}
      {featuredGames.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Featured Matchups
            </Typography>
            <Button onClick={() => navigate('/odds')} sx={{ fontWeight: 700 }}>
              See all odds
            </Button>
          </Box>
          <OddsList odds={featuredGames} selectedSport={null} isHomeScreen />
        </Box>
      )}

      {/* Sports News Section */}
      <Box sx={{ mt: 4 }}>
        <SportsNews />
      </Box>

      {/* Promos Section */}
      <Box sx={{ mt: 4 }}>
        <Promos />
      </Box>
    </Box>
  );
};

export default HomeScreen;
